import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";

export default function WaitlistModal({ open, onClose, product }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!open || !product) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await joinWaitlist({ email: email.trim().toLowerCase(), name: name.trim(), product_slug: product.slug });
      setDone(true);
      toast.success("You're on the list.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something didn't go through.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center px-6 bg-ink/60 backdrop-blur-sm"
      data-testid="waitlist-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-md bg-bone border border-hairline p-10 md:p-12"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          data-testid="waitlist-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-charcoal hover:text-ink transition-colors"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-6">
            <p className="overline mb-6 text-moss">You're on the list</p>
            <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-4">
              We'll be in <em className="italic">quiet</em> touch.
            </h3>
            <p className="text-charcoal font-light leading-relaxed mb-8">
              You're among the first to know when <span className="italic">{product.name}</span> is ready.
              No marketing. One considered email, when the product opens.
            </p>
            <button
              onClick={onClose}
              className="text-[0.72rem] tracking-[0.24em] uppercase bg-ink text-bone px-6 py-3 hover:bg-charcoal transition-colors"
              data-testid="waitlist-done"
            >
              Return to the ritual
            </button>
          </div>
        ) : (
          <>
            <p className="overline mb-4 text-moss">Coming soon · Join the waitlist</p>
            <h3 className="font-display text-3xl md:text-4xl text-ink leading-tight mb-3">
              {product.name}
            </h3>
            <p className="text-charcoal font-light leading-relaxed text-sm mb-8">
              {product.tagline || "A quiet signal when it opens."}
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="waitlist-form">
              <div>
                <label className="block text-[0.62rem] tracking-[0.28em] uppercase text-charcoal mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="waitlist-name"
                  className="w-full border-b border-hairline bg-transparent py-2 text-ink outline-none focus:border-ink transition-colors"
                />
              </div>
              <div>
                <label className="block text-[0.62rem] tracking-[0.28em] uppercase text-charcoal mb-2">
                  Email
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="waitlist-email"
                  className="w-full border-b border-hairline bg-transparent py-2 text-ink outline-none focus:border-ink transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit"
                className="w-full mt-4 bg-ink text-bone py-3 text-[0.72rem] tracking-[0.28em] uppercase font-medium hover:bg-charcoal transition-colors disabled:opacity-50"
              >
                {loading ? "Sending…" : "Join the waitlist"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
